import { api } from "@/shared/api/client";
import type { AuthTokens, Operator } from "./types";

// Existing backend auth flow (no new endpoint):
//   1) POST /api/users/login        { phone_number, hash }  -> sends OTP
//   2) POST /api/users/verify-code  { phone_number, code } -> { access, refresh, user }

// The backend's LoginRequestSerializer requires a non-blank `hash` (the Android
// SMS-Retriever app hash used by the mobile apps for OTP auto-fill). The web
// dashboard has no SMS auto-read, so we send a constant placeholder — it is only
// forwarded to the SMS sender and is otherwise irrelevant here. Omitting it makes
// /users/login return 400 {"hash":["This field is required."]}.
const WEB_OTP_HASH = "web-dashboard";

export async function requestCode(phoneNumber: string): Promise<void> {
  await api.post("/users/login", { phone_number: phoneNumber, hash: WEB_OTP_HASH });
}

export async function verifyCode(
  phoneNumber: string,
  code: string,
): Promise<AuthTokens> {
  const res = await api.post<AuthTokens>("/users/verify-code", {
    phone_number: phoneNumber,
    code,
  });
  return res.data;
}

// Raw shape from GET /api/support/me/ — backend serializer uses model field names.
interface MeResponse {
  id: number;
  first_name: string;
  last_name: string;
  phone_number: string;
  is_staff: boolean;
}

// Support guard: 200 = staff operator, 403 = no support rights.
// Map the backend field names (first_name/last_name/phone_number) onto the UI's
// Operator shape (name/phone) at this boundary.
export async function fetchMe(): Promise<Operator> {
  const res = await api.get<MeResponse>("/support/me/");
  const d = res.data;
  return {
    id: d.id,
    name: `${d.first_name ?? ""} ${d.last_name ?? ""}`.trim() || d.phone_number,
    phone: d.phone_number,
    is_staff: d.is_staff,
  };
}
